"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import clsx from "clsx";
import { useLocale, useTranslations } from "next-intl";
import TopBar from "@/ui/layout/header/topBar";
import { AIRPORTS } from "../airports";

/**
 * Cabecera de la red de aeropuertos. `/aeropuertos` queda fuera del
 * `LayoutWrapper` —no lleva el header del sitio— y los portales regionales
 * tienen el suyo, así que esta página necesita el propio: misma barra de
 * portales y mismo tratamiento translúcido sobre el hero que el sitio principal.
 */
export default function AeropuertosNav() {
  const t = useTranslations("aeropuertosPage");
  const locale = useLocale();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={clsx(
        "fixed top-0 left-0 right-0 z-[70] transition-all duration-300",
        isScrolled
          ? "bg-[var(--fondoGeneral-secciones)] shadow-lg border-b-2 border-[var(--acento-nav)]"
          : // Sobre el hero: velo denso arriba que se disuelve, igual que en el
            // header del sitio. Sin `filter`, que rompería el `fixed`.
            "bg-[linear-gradient(to_bottom,rgba(12,29,53,0.95)_0%,rgba(16,37,66,0.82)_55%,rgba(16,37,66,0.45)_85%,transparent_100%)]",
      )}
    >
      <TopBar isScrolled={isScrolled} />

      <nav
        className={clsx(
          "w-[92%] max-w-7xl mx-auto flex items-center justify-between gap-4 transition-all duration-300",
          isScrolled ? "py-3" : "py-4",
        )}
      >
        <Link
          href={`/${locale}`}
          className="shrink-0 flex items-center gap-2 transition-transform duration-200 hover:scale-105"
        >
          <Image
            src="/Iso_Blanco.svg"
            alt="logo tocumen"
            width={100}
            height={100}
            className={clsx(
              "w-auto object-contain transition-all duration-300",
              isScrolled ? "h-8" : "h-10",
            )}
            priority
            unoptimized
          />
          <span className="text-[var(--blanco)] uppercase text-sm font-bold border-l border-[var(--blanco)] pl-2">
            Tocumen
          </span>
        </Link>

        {/* El código IATA basta en móvil; la ciudad entra cuando hay sitio */}
        <ul className="flex items-center gap-4 sm:gap-6">
          {AIRPORTS.map(({ id, code, href }) => (
            <li key={id}>
              <Link
                href={`/${locale}${href === "/" ? "" : href}`}
                title={t(`airports.${id}.name`)}
                className="group flex items-baseline gap-2 text-[var(--blanco)]/70 hover:text-[var(--blanco)] transition-colors duration-200"
              >
                <span className="text-sm font-bold tracking-wide">{code}</span>
                <span className="hidden lg:inline text-xs text-[var(--blanco)]/50 group-hover:text-[var(--blanco)]/70 transition-colors duration-200">
                  {t(`airports.${id}.city`)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </header>
  );
}
